/**
 * App Flow Walkthrough — „klickbarer" Produkt-Flow als Video.
 *
 * Zeigt mehrere App-/Website-Screenshots (inputs.media, geordnet) in einem
 * realistischen Phone- oder Browser-Frame. Pro Screen fährt ein animierter
 * Cursor zu einem frei definierbaren KLICKPUNKT (Hotspot), „tippt" (Ripple),
 * und der nächste Screen wird eingeblendet (Slide / Fade / Up) — so wirkt es
 * wie ein echter Tab-Wechsel bzw. Sprung in die Händler-/Produkt-Ansicht.
 *
 * Klickpunkte & Captions werden über `items` definiert (eine Zeile pro Screen):
 *   "Caption | xPct,yPct | transition"
 *     - Caption     → Erklärtext für diesen Schritt (z. B. „Tippe auf Händler")
 *     - xPct,yPct   → Klickpunkt in % der Screen-Fläche (0–100), z. B. "62,28"
 *     - transition  → Übergang ZUM nächsten Screen: slide | fade | up (Default slide)
 *   (Alle Teile optional — fehlt der Hotspot, tippt der Cursor mittig-unten.)
 *
 * Inputs:
 *   - media        → geordnete Screenshot-/Video-URLs (die Screens)
 *   - items        → pro Screen "Caption | x,y | transition"
 *   - text         → Headline über dem Frame (optional)
 *   - accent       → Akzent-Token
 *   - extras.device → "phone" (Default) | "browser"
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
  pickMediaSrc, isVideoMedia,
} from "../../reel/sceneShared.js";
import { screenContent, splitItem } from "./_mockHelpers.js";

type Step = { caption: string; x: number; y: number; transition: "slide" | "fade" | "up" };

export const appFlowWalkthrough: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "";
  const device = String(inputs.extras?.device ?? "phone") === "browser" ? "browser" : "phone";

  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);

  // Screens (geordnet). Ohne Media → ein einzelner Fallback-Screen.
  const media = (inputs.media ?? []).filter((m): m is string => Boolean(m && m.trim()));
  const nScreens = Math.max(media.length, 1);

  // Pro-Schritt-Config aus items parsen.
  const steps: Step[] = [];
  for (let i = 0; i < nScreens; i++) {
    const raw = inputs.items?.[i] ?? "";
    const [capRaw, posRaw, transRaw] = splitItem(raw);
    let x = 50, y = 72;
    if (posRaw) {
      const [xs, ys] = posRaw.split(",").map((s) => parseFloat(s.trim()));
      if (Number.isFinite(xs)) x = Math.max(4, Math.min(96, xs));
      if (Number.isFinite(ys)) y = Math.max(4, Math.min(96, ys));
    }
    const tr = (transRaw ?? "").trim();
    const transition: Step["transition"] = tr === "fade" ? "fade" : tr === "up" ? "up" : "slide";
    steps.push({ caption: (capRaw ?? "").trim(), x, y, transition });
  }

  // Frame-Dimensionen.
  const phoneW = isVertical ? 452 : ctx.format === "1:1" ? 410 : 372;
  const bezel = Math.round(phoneW * 0.035);
  const phoneInnerW = phoneW - bezel * 2;
  const phoneInnerH = Math.round(phoneInnerW * (2796 / 1290));
  const phoneH = phoneInnerH + bezel * 2;

  const browserW = isVertical ? 920 : ctx.format === "1:1" ? 880 : 1180;
  const browserH = Math.round(browserW * (isVertical ? 1.32 : 0.62));
  const chromeH = Math.round(browserH * 0.085);

  const frameW = device === "phone" ? phoneW : browserW;
  const frameH = device === "phone" ? phoneH : browserH;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const deviceId = `${clipPrefix}-device`;
  const glowId = `${clipPrefix}-glow`;
  const cursorId = `${clipPrefix}-cursor`;
  const pressId = `${clipPrefix}-press`;
  const rippleId = `${clipPrefix}-ripple`;
  const layerId = (i: number) => `${clipPrefix}-layer-${i}`;
  const capId = (i: number) => `${clipPrefix}-cap-${i}`;

  // Screen-Layer (gestapelt, randlos). Echte Screenshots → object-fit cover.
  const layersHtml = Array.from({ length: nScreens }, (_, i) => {
    const inner = screenContent({
      media: media[i] ?? null,
      accent,
      fallback: i === 0 ? "list" : i === nScreens - 1 ? "card" : "feed",
      objectFit: device === "phone" ? "cover" : "cover",
    });
    return `<div id="${layerId(i)}" style="position:absolute;inset:0;background:${themeColors.bg};overflow:hidden;will-change:transform,opacity;">${inner}</div>`;
  }).join("");

  // Tippende HAND (Finger), nicht Pfeil-Cursor. Fingerspitze = Klickpunkt.
  // Struktur: aussen (left/top animiert) → Anchor (statisches translate, damit
  // die Fingerspitze exakt auf dem Hotspot liegt) → Hand-SVG (Press-Scale).
  const curW = Math.round(frameW * 0.13);
  const curH = Math.round(curW * 1.5);
  // Fingerspitze im viewBox (50x76) bei ~ (16,9) → in % der Box.
  const tipXpct = (16 / 50) * 100;
  const tipYpct = (9 / 76) * 100;
  const rippleSz = Math.round(curW * 1.25);
  // Hand-Silhouette (gefüllt, weicher Schatten + feiner Rand) – Zeigefinger nach oben.
  const handPath = "M12 30 V13 a4 4 0 0 1 8 0 v13 h2 V20 a4 4 0 0 1 8 0 v7 h2 a4 4 0 0 1 4 4 v13 a18 18 0 0 1 -18 18 a16 16 0 0 1 -13 -7 l-8 -12 a4.5 4.5 0 0 1 6.4 -5.4 l4.6 4.6 z";
  const cursorHtml = `<div id="${cursorId}" style="position:absolute;left:50%;top:92%;z-index:20;pointer-events:none;will-change:left,top;">
    <div style="position:relative;transform:translate(-${tipXpct.toFixed(2)}%,-${tipYpct.toFixed(2)}%);width:${curW}px;height:${curH}px;">
      <div id="${rippleId}" style="position:absolute;left:${tipXpct.toFixed(2)}%;top:${tipYpct.toFixed(2)}%;width:${rippleSz}px;height:${rippleSz}px;margin-left:-${Math.round(rippleSz / 2)}px;margin-top:-${Math.round(rippleSz / 2)}px;border-radius:9999px;background:${withAlpha(accent, 0.45)};box-shadow:0 0 26px ${withAlpha(accent, 0.7)};opacity:0;"></div>
      <svg id="${pressId}" width="${curW}" height="${curH}" viewBox="0 0 50 76" style="position:absolute;left:0;top:0;transform-origin:${tipXpct.toFixed(2)}% ${tipYpct.toFixed(2)}%;filter:drop-shadow(0 8px 16px rgba(0,0,0,0.6));"><path d="${handPath}" fill="#f6f8fb" stroke="#0b1117" stroke-width="2.2" stroke-linejoin="round"/></svg>
    </div>
  </div>`;

  const screenInner = `<div id="${clipPrefix}-screen" style="position:relative;width:100%;height:100%;overflow:hidden;background:${themeColors.bg};border-radius:${device === "phone" ? Math.round(phoneInnerW * 0.11) : 0}px;">
    ${layersHtml}
    ${cursorHtml}
  </div>`;

  // Frame-Markup.
  let frameHtml: string;
  if (device === "phone") {
    frameHtml = `<div id="${deviceId}" style="position:relative;width:${phoneW}px;height:${phoneH}px;background:linear-gradient(180deg,#1a1a1c 0%,#0a0a0b 100%);border-radius:${Math.round(phoneW * 0.13)}px;padding:${bezel}px;box-shadow:0 0 90px ${withAlpha(accent, 0.35)},0 60px 130px rgba(0,0,0,0.55),inset 0 0 0 1.5px ${withAlpha(themeColors.fg, 0.08)};border:1.5px solid ${withAlpha(themeColors.fg, 0.18)};box-sizing:border-box;will-change:transform,opacity;">
      ${screenInner}
    </div>`;
  } else {
    const dot = (c: string) => `<span style="width:${Math.round(chromeH * 0.22)}px;height:${Math.round(chromeH * 0.22)}px;border-radius:9999px;background:${c};display:inline-block;"></span>`;
    frameHtml = `<div id="${deviceId}" style="position:relative;width:${browserW}px;height:${browserH}px;background:#0e1117;border-radius:18px;box-shadow:0 0 90px ${withAlpha(accent, 0.32)},0 60px 130px rgba(0,0,0,0.55);border:1.5px solid ${withAlpha(themeColors.fg, 0.14)};overflow:hidden;box-sizing:border-box;will-change:transform,opacity;">
      <div style="height:${chromeH}px;display:flex;align-items:center;gap:10px;padding:0 ${Math.round(chromeH * 0.5)}px;background:linear-gradient(180deg,#1c2129,#11151c);border-bottom:1px solid ${withAlpha(themeColors.fg, 0.08)};">
        ${dot("#ff5f57")}${dot("#febc2e")}${dot("#28c840")}
        <div style="flex:1;margin:0 ${Math.round(chromeH * 0.4)}px;height:${Math.round(chromeH * 0.5)}px;border-radius:9999px;background:${withAlpha(themeColors.fg, 0.08)};display:flex;align-items:center;padding:0 14px;font-family:'Inter',sans-serif;font-size:${Math.round(chromeH * 0.32)}px;color:${withAlpha(themeColors.fg, 0.6)};">allyver.com</div>
      </div>
      <div style="position:relative;height:${browserH - chromeH}px;">${screenInner}</div>
    </div>`;
  }

  const captionsHtml = steps.map((s, i) =>
    s.caption
      ? `<div id="${capId(i)}" style="position:absolute;left:50%;bottom:0;transform:translateX(-50%);display:inline-flex;align-items:center;gap:10px;background:${withAlpha(themeColors.bg, 0.72)};border:1px solid ${withAlpha(accent, 0.4)};border-radius:9999px;padding:${isVertical ? 14 : 12}px ${isVertical ? 26 : 22}px;backdrop-filter:blur(8px);white-space:nowrap;">
          <span style="width:9px;height:9px;border-radius:9999px;background:${accent};box-shadow:0 0 12px ${accent};"></span>
          <span style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.62}px;letter-spacing:0.2px;color:${themeColors.fg};">${escapeHtml(s.caption)}</span>
        </div>`
      : "",
  ).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 36 : 28}px;padding:${isVertical ? "120px 60px" : "70px"};box-sizing:border-box;">
    ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.66}px;letter-spacing:-1.5px;line-height:1.04;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 820 : 1100}px;">${escapeHtml(headline)}</div>` : ""}
    <div style="position:relative;display:flex;justify-content:center;">
      <div id="${glowId}" style="position:absolute;inset:-40px;border-radius:140px;background:radial-gradient(closest-side, ${withAlpha(accent, 0.4)} 0%, transparent 70%);filter:blur(30px);"></div>
      ${frameHtml}
    </div>
    <div style="position:relative;width:100%;height:${Math.round(sm * 1.6)}px;">${captionsHtml}</div>
  </div>`;

  // ---- Timeline ----------------------------------------------------------
  const intro = 0.55;
  const tail = 0.45;
  const usable = Math.max(1.2, clipDurationSec - intro - tail);
  const stepDur = usable / nScreens;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 16 }) : "",
    gsapSetLine(`#${deviceId}`, { opacity: 0, y: 64, scale: 0.94, transformOrigin: "50% 60%" }),
    gsapSetLine(`#${glowId}`, { opacity: 0 }),
    gsapSetLine(`#${cursorId}`, { opacity: 0, left: "50%", top: "94%" }),
    gsapSetLine(`#${pressId}`, { scale: 1, transformOrigin: `${tipXpct.toFixed(2)}% ${tipYpct.toFixed(2)}%` }),
    gsapSetLine(`#${rippleId}`, { opacity: 0, scale: 0 }),
    // Erster Layer sichtbar, restliche transparent.
    ...Array.from({ length: nScreens }, (_, i) =>
      gsapSetLine(`#${layerId(i)}`, i === 0 ? { opacity: 1, xPercent: 0, yPercent: 0 } : { opacity: 0, xPercent: 0, yPercent: 0 }),
    ),
    ...steps.map((s, i) => (s.caption ? gsapSetLine(`#${capId(i)}`, { opacity: 0, y: 10 }) : "")),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${deviceId}`, { opacity: 1, y: 0, scale: 1, duration: 0.7, ease: "power3.out" }, 0),
    t.to(`#${glowId}`, { opacity: 0.85, duration: 0.6, ease: "power2.out" }, 0.2),
    t.to(`#${cursorId}`, { opacity: 1, duration: 0.3, ease: "power2.out" }, intro - 0.1),
  ];
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "power2.out" }, 0.3));

  for (let i = 0; i < nScreens; i++) {
    const s = steps[i];
    const sStart = intro + i * stepDur;
    const moveDur = Math.min(0.55, stepDur * 0.4);
    const tapAt = sStart + moveDur + 0.08;

    // Hand zum Hotspot fahren (left/top → kein Transform-Konflikt mit Press-Scale).
    tweens.push(t.to(`#${cursorId}`, { left: `${s.x}%`, top: `${s.y}%`, duration: moveDur, ease: "power3.inOut" }, sStart + 0.05));
    // Tap: Finger drückt (Scale an der Fingerspitze), Ripple ploppt.
    tweens.push(t.to(`#${pressId}`, { scale: 0.8, duration: 0.12, ease: "power2.in" }, tapAt));
    tweens.push(t.to(`#${pressId}`, { scale: 1, duration: 0.2, ease: "back.out(2)" }, tapAt + 0.12));
    tweens.push(t.fromTo(`#${rippleId}`, { opacity: 0.7, scale: 0.2 }, { opacity: 0, scale: 2.8, duration: 0.5, ease: "power2.out" }, tapAt));

    // Caption ein-/ausblenden.
    if (s.caption) {
      tweens.push(t.to(`#${capId(i)}`, { opacity: 1, y: 0, duration: 0.35, ease: "power2.out" }, sStart + 0.1));
      tweens.push(t.to(`#${capId(i)}`, { opacity: 0, y: -8, duration: 0.3, ease: "power2.in" }, sStart + stepDur - 0.28));
    }

    // Übergang zum nächsten Screen.
    if (i < nScreens - 1) {
      const trAt = sStart + stepDur - 0.42;
      const trDur = Math.min(0.5, stepDur * 0.4);
      const next = `#${layerId(i + 1)}`;
      if (s.transition === "fade") {
        tweens.push(t.set(next, { opacity: 0, xPercent: 0, yPercent: 0 }, trAt));
        tweens.push(t.to(next, { opacity: 1, duration: trDur, ease: "power2.out" }, trAt));
        tweens.push(t.to(`#${layerId(i)}`, { opacity: 0, duration: trDur, ease: "power2.in" }, trAt + 0.05));
      } else if (s.transition === "up") {
        // Nächster Screen schiebt von unten komplett über den aktuellen.
        tweens.push(t.set(next, { opacity: 1, yPercent: 100, xPercent: 0 }, trAt));
        tweens.push(t.to(next, { yPercent: 0, duration: trDur, ease: "power3.out" }, trAt));
        tweens.push(t.set(`#${layerId(i)}`, { opacity: 0 }, trAt + trDur)); // Vorgänger sauber ausblenden
      } else {
        // slide (Default): nächster Screen schiebt von rechts rein, der aktuelle
        // KOMPLETT nach links aus dem Bild (kein halb stehengebliebener Rest).
        tweens.push(t.set(next, { opacity: 1, xPercent: 100, yPercent: 0 }, trAt));
        tweens.push(t.to(next, { xPercent: 0, duration: trDur, ease: "power3.out" }, trAt));
        tweens.push(t.to(`#${layerId(i)}`, { xPercent: -100, duration: trDur, ease: "power3.inOut" }, trAt));
        tweens.push(t.set(`#${layerId(i)}`, { opacity: 0 }, trAt + trDur)); // danach unsichtbar
      }
    }
  }

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 12));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
